// Provenance: repo:tds-reconciliation-portal:frontend/src/shell/runContext.tsx (ADR-0005) —
// run ids became UUID strings (the predecessor coerced to Number at the edge; there is no
// longer a number to coerce to). Everything else — the URL as the single source of truth,
// the sessionStorage mirror, the withRun composition — carries whole.
import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router'

/** Shared run selection for a period. The URL `?run=` is the single source of
 *  truth; the last explicit choice is mirrored to storage so landing on a
 *  surface WITHOUT `?run=` restores it instead of silently jumping to the
 *  latest run. `withRun(path)` appends the current run so in-page tab links
 *  stop dropping it (the wrong-run hazard).
 *
 *  That mirror is **sessionStorage**, not localStorage. Sides are session-scoped
 *  (see `workspaceSession`) and the run was not, so a fresh login in a new tab
 *  restored `?run=25` and served a previous session's finished reports on a
 *  workspace that claimed to be empty. Two halves of one question cannot use
 *  two different lifetimes; within a session nothing changes. */
/** The one place the run key is spelled. Exported so the writer, the reader
 *  and the login sweep cannot drift: a mismatch here fails silently — the run
 *  is remembered and then never found. */
export const runStorageKey = (periodId: string) => `tds.lastRun.${periodId}`
const key = runStorageKey

/** The run a just-finished job should hand over to, or null.
 *
 *  A run this session created is the reconciler's own action, minutes old, and
 *  every surface should open on it. A run from a PREVIOUS session is inherited
 *  state — the thing `clearSessionState` exists to remove — so this is only ever
 *  called with a run the session started itself.
 *
 *  Only a finished one. Adopting a queued or running job points every surface at
 *  a run with no rows yet; adopting a failed one lands the reconciler on error
 *  states they never asked for.
 */
export function adoptableRun(run: { id: string; status: string } | null | undefined) {
  return run && run.status === 'complete' ? run.id : null
}

/** Make `runId` the session's run for `periodId`.
 *
 *  Writes the key `useRunSelection` already reads, in sessionStorage, which the
 *  session-end sweep clears (`lib/session-end.ts`, ADR-0012). Nothing new is
 *  persisted and no new lifetime is introduced — "open what I just made"
 *  therefore cannot decay into "open what somebody made yesterday".
 *
 *  **That sweep had no caller until 2026-09-10.** This comment asserted it for
 *  four phases while `clearSessionState` was reachable only from its own tests,
 *  so the decay it rules out was live the whole time. Wired at the one place a
 *  session actually ends.
 *
 *  A newer run replaces an older one deliberately: opening an old run to check
 *  history and then reconciling afresh means the new run is the current work.
 */
export function rememberRun(periodId: string | null, runId: string,
                            store: Storage = sessionStorage) {
  if (periodId == null) return
  try {
    store.setItem(key(periodId), String(runId))
  } catch {
    /* privacy mode / locked-down profile — not worth failing a run over */
  }
}

/** Add `run` to a path that may already carry a query string.
 *
 *  This used to be `` `${path}?run=${current}` ``, which assumed no path ever
 *  had one. The Reconciliation surface has a `?tab=` deep link, so every tab
 *  link would have become `…?tab=sheet?run=10` — a single parameter named
 *  `tab` whose value is `sheet?run=10`, silently dropping the run and landing
 *  the user on a different run's data with no indication.
 *
 *  Exported and pure so it can be tested without a browser: getting a URL
 *  wrong is not the kind of thing a type checker notices. */
export function withRunParam(path: string, run: string | null): string {
  if (!run) return path
  const [base, query = ''] = path.split('?')
  const params = new URLSearchParams(query)
  params.set('run', run)
  return `${base}?${params.toString()}`
}

export function useRunSelection(periodId: string | null) {
  const [params, setParams] = useSearchParams()
  const urlRun = params.get('run')
  const stored = periodId != null ? safeGet(key(periodId)) : null
  const current = urlRun ?? stored ?? null

  // remember an explicit URL run so other surfaces can restore it
  useEffect(() => {
    if (urlRun && periodId != null) safeSet(key(periodId), urlRun)
  }, [urlRun, periodId])

  const setRun = useCallback(
    (id: string) => {
      const v = String(id)
      if (periodId != null) safeSet(key(periodId), v)
      const next = new URLSearchParams(params)
      next.set('run', v)
      setParams(next, { replace: true })
    },
    [params, periodId, setParams],
  )

  const withRun = useCallback(
    (path: string) => withRunParam(path, current),
    [current],
  )

  return { runId: current, setRun, withRun }
}

function safeGet(k: string): string | null {
  try {
    return sessionStorage.getItem(k)
  } catch {
    return null
  }
}

function safeSet(k: string, v: string) {
  try {
    sessionStorage.setItem(k, v)
  } catch {
    /* ignore */
  }
}
